/**
 * Site About → Webhooks: one setup flow for a site webhook (ENG-2427 item 1).
 *
 * Add or edit the destination (URL, method, auth), pick which cameras send
 * to it and on which events, and build the payload with a live preview — so
 * nobody has to visit each camera's Alert Settings. Save writes the site
 * webhook, then adds, removes or re-types its stream links. Only link
 * changes that touch event types reach the connector settings, so only those
 * mark the site unsynced (admin marks it undeployed by the same rule).
 */
import React from "react";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";
import {
  Card,
  CardHeader,
  Hint,
  TS_COLORS,
  TsButton,
} from "../../Troubleshooting/TroubleshootingUi";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import {
  EMPTY_SITE_WEBHOOK_FORM_VALUES,
  SiteWebhookFormFields,
  SiteWebhookFormValues,
} from "../../../common/SiteWebhookFormFields";
import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import {
  WebhookAuthField,
  WebhookFieldErrors,
  applyWebhookAuthFieldToPatch,
  authBodyJsonError,
  extractWebhookFieldErrors,
  pickWebhookAuthValues,
} from "../../../utils/webhookAuth";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import {
  SiteWebhook,
  buildSiteWebhookCreatePayload,
  jsonTextError,
} from "./SiteAboutPageUtils/siteWebhooks";
import {
  DELIVERED_EVENT_TYPES,
  EVENT_TYPE_HINT,
  EVENT_TYPE_LABEL,
} from "./SiteAboutPageUtils/webhookPayload";
import {
  SiteStream,
  WebhookLink,
  applyLinkPlan,
  fetchSiteStreams,
  fetchSiteWebhookLinks,
  initialEventTypes,
  linksDisagree,
  planLinkChanges,
  readEventTypeOptions,
  readSiteCameraNames,
  streamLabel,
} from "./SiteAboutPageUtils/webhookLinks";
import { WebhookPayloadEditor } from "./WebhookPayloadEditor";

const formValuesOf = (
  webhook: SiteWebhook | undefined
): SiteWebhookFormValues =>
  webhook
    ? {
        display_name: webhook.display_name ?? "",
        target_url: webhook.target_url ?? "",
        method: webhook.method || "POST",
        message_body: webhook.message_body || "{}",
        auth: pickWebhookAuthValues(webhook),
      }
    : EMPTY_SITE_WEBHOOK_FORM_VALUES;

const AUTH_FIELDS: WebhookAuthField[] = [
  "auth_type",
  "auth_endpoint",
  "auth_body",
  "auth_token_path",
];

/** PATCH body for an edited site webhook: only the fields that changed. */
export const buildSiteWebhookPatch = (
  saved: SiteWebhook,
  values: SiteWebhookFormValues
): Record<string, unknown> => {
  const savedValues = formValuesOf(saved);
  let patch: Record<string, unknown> = {};
  (["display_name", "target_url", "method", "message_body"] as const).forEach(
    (field) => {
      if (values[field] !== savedValues[field]) patch[field] = values[field];
    }
  );
  AUTH_FIELDS.forEach((field) => {
    patch = applyWebhookAuthFieldToPatch(
      patch,
      field,
      values.auth[field],
      savedValues.auth[field]
    );
  });
  return patch;
};

const describeError = (reason: AxiosError | any): string => {
  const status = reason?.response?.status;
  const data = reason?.response?.data;
  if (status === 403) return "You don't have permission to change webhooks.";
  if (status === 404)
    return "That webhook or camera is no longer on this site.";
  if (data && typeof data === "object") {
    if (typeof data.detail === "string") return data.detail;
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first.length > 0) return String(first[0]);
    if (typeof first === "string") return first;
  }
  return "Saving the webhook failed. Please try again.";
};

const Section = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card>
    <CardHeader title={title} hint={hint} />
    <Box sx={{ p: 2.5, backgroundColor: "#fff" }}>{children}</Box>
  </Card>
);

export const WebhookSetupDialog = ({
  siteId,
  webhooks,
  navigate,
  onSaved,
}: {
  siteId: number;
  webhooks: SiteWebhook[];
  navigate: NavigateFunction;
  onSaved: () => void;
}) => {
  const addOpen = useSiteAboutStore((state) => state.addSiteWebhookDialogOpen);
  const setAddOpen = useSiteAboutStore(
    (state) => state.setAddSiteWebhookDialogOpen
  );
  const editId = useSiteAboutStore((state) => state.editSiteWebhookId);
  const setEditId = useSiteAboutStore((state) => state.setEditSiteWebhookId);
  const open = addOpen || editId !== null;
  const editing = webhooks.find((webhook) => webhook.id === editId);

  const [values, setValues] = React.useState<SiteWebhookFormValues>(
    EMPTY_SITE_WEBHOOK_FORM_VALUES
  );
  // Bumped when the form is (re)filled, so the payload editor restarts from
  // the loaded template instead of keeping builder state from the last one.
  const [formKey, setFormKey] = React.useState(0);
  const [fieldErrors, setFieldErrors] = React.useState<WebhookFieldErrors>({});
  const [streams, setStreams] = React.useState<SiteStream[]>([]);
  const [links, setLinks] = React.useState<WebhookLink[]>([]);
  const [selectedStreamIds, setSelectedStreamIds] = React.useState<number[]>(
    []
  );
  const [eventTypes, setEventTypes] = React.useState<string[]>([]);
  const [eventTypesChanged, setEventTypesChanged] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Set once a create has gone through, so a retry after a failed link step
  // edits that webhook instead of creating a second one.
  const [createdWebhook, setCreatedWebhook] =
    React.useState<SiteWebhook | null>(null);

  const eventTypeOptions = React.useMemo(readEventTypeOptions, [open]);
  const cameraNames = React.useMemo(readSiteCameraNames, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setValues(formValuesOf(editing));
    setFormKey((previous) => previous + 1);
    setFieldErrors({});
    setSaveError(null);
    setLoadError(null);
    setCreatedWebhook(null);
    setEventTypesChanged(false);
    setLoading(true);
    Promise.all([
      fetchSiteStreams(siteId),
      editId !== null ? fetchSiteWebhookLinks(siteId) : Promise.resolve([]),
    ]).then(
      ([siteStreams, siteLinks]) => {
        if (cancelled) return;
        const ownLinks = siteLinks.filter(
          (link) => link.webhook?.id === editId
        );
        setStreams(siteStreams);
        setLinks(ownLinks);
        setSelectedStreamIds(ownLinks.map((link) => link.stream.id));
        setEventTypes(
          ownLinks.length > 0 ? initialEventTypes(ownLinks) : ["detection"]
        );
        setLoading(false);
      },
      (reason: AxiosError) => {
        if (cancelled) return;
        setLoading(false);
        setLoadError(
          `Couldn't load this site's cameras: ${describeError(reason)}`
        );
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editId, siteId]);

  const close = () => {
    setAddOpen(false);
    setEditId(null);
  };

  const cancel = () => {
    close();
    // A create that went through before a failed camera step still exists.
    if (createdWebhook) onSaved();
  };

  const shownEventTypes = [
    ...DELIVERED_EVENT_TYPES,
    // Keep any other type already on a link visible so it isn't dropped unseen.
    ...eventTypes.filter(
      (value) => !(DELIVERED_EVENT_TYPES as string[]).includes(value)
    ),
  ];

  const toggleEventType = (value: string, checked: boolean) => {
    setEventTypes((previous) =>
      checked
        ? [...previous.filter((item) => item !== value), value]
        : previous.filter((item) => item !== value)
    );
    setEventTypesChanged(true);
  };

  const toggleStream = (streamId: number, checked: boolean) => {
    setSelectedStreamIds((previous) =>
      checked
        ? [...previous.filter((id) => id !== streamId), streamId]
        : previous.filter((id) => id !== streamId)
    );
  };

  const allSelected =
    streams.length > 0 && selectedStreamIds.length === streams.length;

  const messageBodyError =
    fieldErrors.message_body ||
    jsonTextError(values.message_body, "Message Body") ||
    undefined;

  const saveDisabled =
    saving ||
    loading ||
    Boolean(loadError) ||
    !values.display_name.trim() ||
    !values.target_url.trim() ||
    Boolean(authBodyJsonError(values.auth.auth_body)) ||
    Boolean(messageBodyError);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    const AxiosUI = createApi("");
    const existing = editing ?? createdWebhook;
    let webhookId = existing?.id ?? null;
    let step: "webhook" | "links" = "webhook";
    try {
      if (existing) {
        const patch = buildSiteWebhookPatch(existing, values);
        if (Object.keys(patch).length > 0) {
          await AxiosUI.patch(`webhook_customer/${existing.id}/`, patch);
        }
      } else {
        const response = await AxiosUI.post(
          "webhook_customer/",
          buildSiteWebhookCreatePayload(siteId, values)
        );
        webhookId = response.data.id;
        setCreatedWebhook(response.data);
      }
      step = "links";
      const plan = planLinkChanges({
        existingLinks: links,
        selectedStreamIds,
        eventTypes,
        eventTypesChanged,
      });
      await applyLinkPlan(
        Number(webhookId),
        plan,
        eventTypes,
        eventTypeOptions
      );
      if (plan.needsSync) markSiteUnsynced(String(siteId));
      setSaving(false);
      close();
      onSaved();
    } catch (reason) {
      setSaving(false);
      const error = reason as AxiosError;
      if (step === "webhook") {
        const nextFieldErrors = extractWebhookFieldErrors(error);
        if (Object.keys(nextFieldErrors).length > 0) {
          setFieldErrors(nextFieldErrors);
          setSaveError("Fix the highlighted fields and save again.");
          return;
        }
      }
      if (error?.response?.status === 401) {
        universalAPIErrorHandler(error, navigate, save, () => {
          setSaveError(describeError(error));
        });
        return;
      }
      setSaveError(
        step === "links"
          ? `The webhook was saved, but updating its cameras failed: ${describeError(
              error
            )} Save again to retry.`
          : describeError(error)
      );
      if (step === "links") {
        // Some link requests may have landed; reload so a retry plans from
        // what is actually there.
        fetchSiteWebhookLinks(siteId).then(
          (siteLinks) =>
            setLinks(
              siteLinks.filter((link) => link.webhook?.id === webhookId)
            ),
          () => undefined
        );
      }
    }
  };

  const selectedWithoutEvents =
    selectedStreamIds.length > 0 && eventTypes.length === 0;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : cancel}
      fullWidth
      maxWidth="lg"
      aria-labelledby="webhook-setup-dialog-title"
    >
      <DialogTitle
        id="webhook-setup-dialog-title"
        sx={{ ...MULISH, fontWeight: 800, color: TS_COLORS.navy }}
      >
        {editing ? `Edit webhook: ${editing.display_name}` : "Add site webhook"}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          data-testid="webhook-setup-dialog"
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          <Section
            title="Destination"
            hint="Where events are sent. Changes here apply on the next event; no Sync needed."
          >
            <SiteWebhookFormFields
              values={values}
              fieldErrors={fieldErrors}
              width={520}
              hideMessageBody
              onFieldChange={(field, value) => {
                setValues((previous) => ({ ...previous, [field]: value }));
              }}
              onAuthFieldChange={(field, value) => {
                setValues((previous) => ({
                  ...previous,
                  auth: { ...previous.auth, [field]: value },
                }));
              }}
            />
          </Section>

          <Section
            title="Cameras and events"
            hint="Which cameras send to this webhook, and when. Changing these needs a Sync."
          >
            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Hint>Loading cameras…</Hint>
              </Box>
            )}
            {loadError && (
              <Hint sx={{ color: TS_COLORS.red }}>{loadError}</Hint>
            )}
            {!loading && !loadError && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 1fr) minmax(0, 2fr)",
                  },
                  gap: 3,
                }}
              >
                <Box data-testid="webhook-event-types">
                  {shownEventTypes.map((value) => (
                    <Box key={value} sx={{ mb: 1 }}>
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={eventTypes.includes(value)}
                            onChange={(event) =>
                              toggleEventType(value, event.target.checked)
                            }
                          />
                        }
                        label={
                          <Box
                            sx={{ ...MULISH, fontSize: 14, fontWeight: 700 }}
                          >
                            {EVENT_TYPE_LABEL[value] ?? value}
                          </Box>
                        }
                      />
                      <Box sx={{ pl: 4 }}>
                        <Hint>{EVENT_TYPE_HINT[value] ?? ""}</Hint>
                      </Box>
                    </Box>
                  ))}
                  {links.length > 1 &&
                    linksDisagree(links) &&
                    !eventTypesChanged && (
                      <Hint sx={{ color: TS_COLORS.amber, display: "block" }}>
                        These cameras currently send different events; each
                        keeps its own until you change the events here, which
                        applies them to every selected camera.
                      </Hint>
                    )}
                </Box>
                <Box data-testid="webhook-streams">
                  {streams.length === 0 ? (
                    <Hint>This site has no camera streams yet.</Hint>
                  ) : (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={
                              selectedStreamIds.length > 0 && !allSelected
                            }
                            onChange={(event) =>
                              setSelectedStreamIds(
                                event.target.checked
                                  ? streams.map((stream) => stream.id)
                                  : []
                              )
                            }
                          />
                        }
                        label={
                          <Box
                            sx={{ ...MULISH, fontSize: 14, fontWeight: 700 }}
                          >
                            All cameras ({selectedStreamIds.length} of{" "}
                            {streams.length} selected)
                          </Box>
                        }
                      />
                      <Box
                        sx={{
                          maxHeight: 280,
                          overflow: "auto",
                          border: `1px solid ${TS_COLORS.line}`,
                          borderRadius: "4px",
                          px: 1,
                        }}
                      >
                        {streams.map((stream) => (
                          <Box key={stream.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedStreamIds.includes(
                                    stream.id
                                  )}
                                  onChange={(event) =>
                                    toggleStream(
                                      stream.id,
                                      event.target.checked
                                    )
                                  }
                                />
                              }
                              label={
                                <Box sx={{ ...MULISH, fontSize: 14 }}>
                                  {streamLabel(stream, cameraNames)}
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                  {selectedWithoutEvents && (
                    <Hint
                      sx={{ color: TS_COLORS.amber, display: "block", mt: 1 }}
                    >
                      No events are ticked, so the selected cameras won&apos;t
                      send anything.
                    </Hint>
                  )}
                </Box>
              </Box>
            )}
          </Section>

          <Section
            title="Payload"
            hint="The JSON your receiver gets. Applies on the next event; no Sync needed."
          >
            <WebhookPayloadEditor
              key={formKey}
              value={values.message_body}
              onChange={(messageBody) =>
                setValues((previous) => ({
                  ...previous,
                  message_body: messageBody,
                }))
              }
              eventTypes={eventTypes}
              targetUrl={values.target_url}
              error={messageBodyError}
            />
          </Section>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: "wrap" }}>
        {saveError && (
          <Box sx={{ mr: "auto" }} data-testid="webhook-setup-error">
            <Hint sx={{ color: TS_COLORS.red }}>{saveError}</Hint>
          </Box>
        )}
        <TsButton variant="quiet" onClick={cancel} disabled={saving}>
          Cancel
        </TsButton>
        <TsButton onClick={save} disabled={saveDisabled}>
          {saving ? "Saving…" : "Save webhook"}
        </TsButton>
      </DialogActions>
    </Dialog>
  );
};
